import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Clock, Share2, Bookmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import SEOHead from '@/components/SEO/SEOHead';
import StructuredData, { createArticleSchema } from '@/components/SEO/StructuredData';
import { getBlogBySlug, getPublishedBlogs } from '@/services/cms';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [readingTime, setReadingTime] = useState(0);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const data = await getBlogBySlug(slug);
        
        if (!data) {
          setError('Blog post not found');
          return;
        }
        
        setBlog(data);
        
        // Calculate reading time (average 200 words per minute)
        const wordCount = data.content.split(/\s+/).length;
        setReadingTime(Math.ceil(wordCount / 200));
        
        // Fetch related blogs
        const allBlogs = await getPublishedBlogs();
        const related = allBlogs
          .filter(b => b.id !== data.id && b.category === data.category)
          .slice(0, 3);
        setRelatedBlogs(related);
      } catch (err) {
        console.error('Error loading blog:', err);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Blog Post Not Found</h1>
          <Button onClick={() => navigate('/blog')}>
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  const seoTitle = blog.seoMetadata?.metaTitle || blog.title;
  const seoDescription = blog.seoMetadata?.metaDescription || blog.excerpt;
  const seoKeywords = blog.seoMetadata?.keywords || [];

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        ogImage={blog.featuredImage}
        ogType="article"
        canonicalUrl={`/blog/${blog.slug}`}
        author={blog.author}
        publishedTime={blog.publishedAt}
        modifiedTime={blog.updatedAt}
      />
      
      <StructuredData data={createArticleSchema(blog)} />

      <div className="min-h-screen bg-white">
        {/* Breadcrumb - Sticky Header */}
        <div className="sticky top-16 z-40 bg-gray-50 border-b border-gray-200 shadow-sm backdrop-blur-sm bg-opacity-95">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <button onClick={() => navigate('/')} className="hover:text-cyan-600 transition-colors">Home</button>
              <span>/</span>
              <button onClick={() => navigate('/blog')} className="hover:text-cyan-600 transition-colors">Blog</button>
              <span>/</span>
              <span className="text-gray-900 truncate">{blog.title}</span>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="max-w-5xl mx-auto px-4 pt-8 pb-6">
          {/* Category Badge */}
          <div className="mb-4">
            <span className="inline-block bg-cyan-100 text-cyan-700 px-4 py-1.5 rounded-full text-sm font-semibold">
              {blog.category}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight font-sans">
            {blog.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <span className="font-medium">by {blog.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{new Date(blog.publishedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>{readingTime} min read</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mb-8">
            <Button variant="outline" size="sm" className="gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Bookmark className="w-4 h-4" />
              Save
            </Button>
          </div>

          {/* Featured Image */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
            <img
              src={blog.featuredImage}
              alt={blog.featuredImageAlt || blog.title}
              className="w-full h-auto object-cover"
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-5xl mx-auto px-4 pb-16">
          {/* Main Content */}
          <div className="blog-content" dangerouslySetInnerHTML={{ __html: blog.content }}>
          </div>

          {/* CTA Section */}
          <Card className="my-16 bg-gradient-to-r from-cyan-600 to-turquoise-600 text-white p-10 text-center rounded-2xl shadow-xl">
            <h3 className="text-3xl font-bold mb-4">Ready to Create Stunning Designs?</h3>
            <p className="text-xl mb-6 text-cyan-50">
              Transform your ideas into professional T-shirt designs in seconds with Fluxara's AI-powered tools.
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate('/generate')}
              className="bg-white text-cyan-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg"
            >
              Start Designing Now
            </Button>
          </Card>

          {/* Related Posts */}
          {relatedBlogs.length > 0 && (
            <div className="mt-20">
              <h2 className="text-3xl font-bold mb-8 text-gray-900">Related Articles</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedBlogs.map((relatedBlog) => (
                  <Card
                    key={relatedBlog.id}
                    className="cursor-pointer hover:shadow-xl transition-all duration-300 overflow-hidden group"
                    onClick={() => {
                      window.scrollTo(0, 0);
                      navigate(`/blog/${relatedBlog.slug}`);
                    }}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={relatedBlog.featuredImage}
                        alt={relatedBlog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-cyan-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                          {relatedBlog.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-cyan-600 transition-colors line-clamp-2">
                        {relatedBlog.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4">{relatedBlog.excerpt}</p>
                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="w-4 h-4 mr-1" />
                        <span>{Math.ceil(relatedBlog.content.split(/\s+/).length / 200)} min read</span>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Back to Blog Button */}
          <div className="mt-16 text-center">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/blog')}
              className="gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to All Articles
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPost;